import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { default as awardsBg } from '../assets/awards_bg.png';
import { default as image1 } from '../assets/image1.JPG';
import { default as image2 } from '../assets/image2.JPG';
import { default as image3 } from '../assets/image3.JPG';
import { default as image3bg } from '../assets/image3-bg.png';
import { logCustomEvent } from '../firebase';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Trophy,
  Eye,
  ChevronDown,
  Users,
  Lightbulb,
  TrendingUp,
  Award,
  ArrowRight,
  Star,
  UserPlus,
  FileText,
  Search,
  Gavel,
  PartyPopper,
  Microscope,
  Play,
  Briefcase,
  Heart,
} from 'lucide-react';
import { default as award6 } from '../assets/sacred.png';


const Home = () => {
  const { lang } = useParams();
  const { t, i18n } = useTranslation('home');
  const currentLang = lang || i18n.language || 'en';



  // Hero Section Component
  const HeroSection = () => {
    const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.3 });
    const heroStats = [
      { value: t('about.combinedStats.editions.value'), suffix: t('about.combinedStats.editions.suffix'), label: t('about.combinedStats.editions.label') },
      { value: t('about.combinedStats.totalAwards.value'), suffix: t('about.combinedStats.totalAwards.suffix'), label: t('about.combinedStats.totalAwards.label') },
      { value: t('about.combinedStats.totalFarmers.value'), suffix: t('about.combinedStats.totalFarmers.suffix'), label: t('about.combinedStats.totalFarmers.label') },
      { value: t('about.combinedStats.totalDistricts.value'), suffix: t('about.combinedStats.totalDistricts.suffix'), label: t('about.combinedStats.totalDistricts.label') },
    ];
    return (
      <Box
        sx={{
          position: 'relative',
          pt: { xs: "140px", lg: "120px" },
          pb: { xs: 6, lg: 8 },
          minHeight: { xs: 'auto', lg: '100vh' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${awardsBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        {/* Overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `
              linear-gradient(180deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.75) 50%, rgba(0, 0, 0, 0.85) 100%)
            `,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', width: '100%'}}>
          <Container maxWidth="xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              style={{ textAlign: 'center', color: 'white' }}
            >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.2rem', md: '3.2rem' },
                    fontWeight: 'bold',
                    lineHeight: 1.1,
                    color: '#D4A747',
                    mb: 1,
                  }}
                >
                  {t('hero.title')}
                </Typography>

                {/* Subtitle & Description */}
                <Box
                  sx={{
                    mt: 1,
                    mb: 4,
                    position: 'relative',
                    width: '100%',
                    mx: 'auto',
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '1rem', md: '1.25rem' },
                        fontWeight: 400,
                        textAlign: 'center',
                        color: 'rgba(244, 228, 188, 0.9)',
                        letterSpacing: 1,
                        lineHeight: 1.4,
                        mb: { xs: 2, md: 2 },
                        maxWidth: '800px',
                        mx: 'auto',
                      }}
                    >
                      {t('hero.subtitle')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.82rem', md: '0.92rem' },
                        fontWeight: 300,
                        textAlign: 'center',
                        color: 'rgba(244, 228, 188, 0.65)',
                        letterSpacing: 0.3,
                        lineHeight: 1.7,
                        mb: { xs: 3, md: 4 },
                        maxWidth: '750px',
                        mx: 'auto',
                      }}
                    >
                      {t('hero.description')}
                    </Typography>
                  </motion.div>

                  {/* Impact heading with decorative lines */}
                  <motion.div
                    initial={{ opacity: 0, y: 25 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3, ease: [0.43, 0.13, 0.23, 0.96] }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
                      <Box sx={{ flex: 1, maxWidth: 80, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(212, 167, 71, 0.5))' }} />
                      <Typography
                        sx={{
                          fontSize: { xs: '0.65rem', md: '0.75rem' },
                          letterSpacing: { xs: 3, md: 5 },
                          textTransform: 'uppercase',
                          color: 'rgba(212, 167, 71, 0.7)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('hero.speakersHeading')}
                      </Typography>
                      <Box sx={{ flex: 1, maxWidth: 80, height: '1px', background: 'linear-gradient(90deg, rgba(212, 167, 71, 0.5), transparent)' }} />
                    </Box>
                  </motion.div>

                  {/* Stats Cards */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.7 }}
                  >
                    <Box
                      ref={statsRef}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'stretch',
                        gap: { xs: 1.5, md: 2.5 },
                        flexWrap: 'wrap',
                        maxWidth: '800px',
                        mx: 'auto',
                        mb: { xs: 3, md: 4 },
                      }}
                    >
                      {heroStats.map((stat, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, scale: 0.8, y: 20 }}
                          animate={statsInView ? { opacity: 1, scale: 1, y: 0 } : {}}
                          transition={{ duration: 0.6, delay: 0.8 + index * 0.15, ease: [0.43, 0.13, 0.23, 0.96] }}
                          whileHover={{ scale: 1.08, y: -4 }}
                          style={{ flex: '1 1 0', minWidth: '120px', display: 'flex' }}
                        >
                          <Box
                            sx={{
                              textAlign: 'center',
                              py: { xs: 2, md: 2.5 },
                              px: { xs: 1.5, md: 2 },
                              borderRadius: '16px',
                              background: 'rgba(255, 255, 255, 0.06)',
                              backdropFilter: 'blur(12px)',
                              border: '1px solid rgba(212, 167, 71, 0.2)',
                              transition: 'all 0.3s ease',
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              '&:hover': {
                                background: 'rgba(212, 167, 71, 0.12)',
                                borderColor: 'rgba(212, 167, 71, 0.4)',
                                boxShadow: '0 8px 32px rgba(212, 167, 71, 0.15)',
                              },
                            }}
                          >
                            <Typography sx={{
                              fontSize: { xs: '1.8rem', md: '2.2rem' },
                              fontWeight: 800,
                              background: 'linear-gradient(180deg, #F4E4BC 0%, #D4A747 100%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              lineHeight: 1,
                            }}>
                              {statsInView && (
                                <CountUp end={stat.value} duration={2.5} suffix={stat.suffix} separator="," />
                              )}
                            </Typography>
                            <Typography sx={{
                              color: 'rgba(244, 228, 188, 0.7)',
                              fontSize: { xs: '0.6rem', md: '0.7rem' },
                              fontWeight: 600,
                              mt: 0.5,
                              letterSpacing: 1.5,
                              textTransform: 'uppercase',
                            }}>
                              {stat.label}
                            </Typography>
                          </Box>
                        </motion.div>
                      ))}
                    </Box>
                  </motion.div>

                </Box>

              {/* CTA Buttons */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, justifyContent: 'center', mb: { xs: 2, md: 2 }, flexWrap: 'wrap', alignItems: 'center', width: '100%', px: { xs: 2, sm: 0 } }}>
                {[
                  { text: t('hero.cta.viewWinners'), link: `/${currentLang}/winners`, icon: 'Trophy', variant: 'contained', external: false },
                  { text: t('hero.cta.knowMore'), link: `/${currentLang}/about`, icon: 'Eye', variant: 'outlined', external: false },
                  { text: t('hero.cta.viewAwards'), link: `/${currentLang}/nominations`, icon: 'Star', variant: 'standard', external: false }
                ].map((button, index) => {
                  const IconComponent = button.icon === 'Trophy' ? Trophy : button.icon === 'Eye' ? Eye : Star;
                  return (
                    <Box key={index} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                        <Button
                          component={button.external ? "a" : Link}
                          {...(button.external ? { href: button.link, target: "_blank", rel: "noopener noreferrer" } : { to: button.link })}
                          variant={button.variant}
                          size="medium"
                          startIcon={<IconComponent size={18} />}
                          onClick={() => {
                            logCustomEvent('cta_button_clicked', {
                              button_text: button.text,
                              button_link: button.link,
                              location: 'hero_section'
                            });
                          }}
                          sx={{
                            ...(button.variant === 'contained' ? {
                              background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                              color: '#1A1A1A',
                              boxShadow: '0 8px 24px rgba(212, 167, 71, 0.25)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #B8901C 0%, #9A7A18 100%)',
                                boxShadow: '0 12px 32px rgba(212, 167, 71, 0.4)',
                              },
                            } : button.variant === 'outlined' ? {
                              borderColor: 'rgba(212, 167, 71, 0.5)',
                              color: '#D4A747',
                              border: '1.5px solid rgba(212, 167, 71, 0.5)',
                              '&:hover': {
                                backgroundColor: '#D4A747',
                                color: '#1A1A1A',
                                borderColor: '#D4A747',
                              },
                            } : {
                              background: 'white',
                              color: '#333333',
                              boxShadow: '0 8px 24px rgba(26, 26, 26, 0.2)',
                              '&:hover': {
                                background: '#f8f9fa',
                                boxShadow: '0 12px 32px rgba(26, 26, 26, 0.3)',
                              },
                            }),
                            width: { xs: '100%', sm: 'auto' },
                            px: 4,
                            py: 1,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '8px',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {button.text}
                        </Button>
                      </motion.div>
                    </Box>
                  );
                })}
              </Box>
            </motion.div>
          </Container>
        </Box>
      </Box>
    );
  };

  // Sponsors Section with Split View
  const SponsorsSection = () => {
    const [slide2024, setSlide2024] = useState(0);
    const [slide2025, setSlide2025] = useState(0);
    const [playingVideo, setPlayingVideo] = useState(null);
    const videoRefs = useRef([]);

    // 2024 speakers with videos - names/titles from translations
    const videos2024 = [
      'https://bhoomee-awards-submissions.s3.ap-south-1.amazonaws.com/videos/sponsor1.mp4',
      'https://bhoomee-awards-submissions.s3.ap-south-1.amazonaws.com/videos/sponsor2.mp4',
      'https://bhoomee-awards-submissions.s3.ap-south-1.amazonaws.com/videos/awards2.mp4',
      'https://bhoomee-awards-submissions.s3.ap-south-1.amazonaws.com/videos/sponsor3.mp4',
    ];
    const translated2024 = t('sponsors.speakers2024', { returnObjects: true }) || [];
    const speakers2024 = translated2024.map((s, i) => ({ ...s, video: videos2024[i], logo: award6 }));

    // 2025 speakers with images - names/titles from translations
    const images2025 = [
      '/images/speakers/speaker-1.png',
      '/images/speakers/speaker-2.png',
      '/images/speakers/speaker-3.png',
      '/images/speakers/speaker-4.png',
      '/images/speakers/speaker-6.png',
    ];
    const translated2025 = t('sponsors.speakers2025', { returnObjects: true }) || [];
    const speakers2025 = translated2025.map((s, i) => ({ ...s, image: images2025[i], logo: award6 }));

    const handleVideoPlay = (index) => {
      if (playingVideo !== null && playingVideo !== index) {
        videoRefs.current[playingVideo]?.pause();
      }
      setPlayingVideo(index);
      const video = videoRefs.current[index];
      if (video) {
        video.muted = true;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            video.muted = false;
          }).catch(() => {
            video.muted = true;
            video.play();
          });
        }
      }
    };

    const handleVideoPause = () => setPlayingVideo(null);

    const next2024 = () => { if (playingVideo !== null) { videoRefs.current[playingVideo]?.pause(); setPlayingVideo(null); } setSlide2024((p) => (p + 1) % speakers2024.length); };
    const prev2024 = () => { if (playingVideo !== null) { videoRefs.current[playingVideo]?.pause(); setPlayingVideo(null); } setSlide2024((p) => (p - 1 + speakers2024.length) % speakers2024.length); };
    const next2025 = () => setSlide2025((p) => (p + 1) % speakers2025.length);
    const prev2025 = () => setSlide2025((p) => (p - 1 + speakers2025.length) % speakers2025.length);

    const ArrowBtn = ({ onClick, direction, dark, golden }) => (
      <Box onClick={onClick} sx={{ width: 36, height: 36, borderRadius: '50%', background: golden ? 'rgba(26, 26, 26, 0.25)' : dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.3s ease', '&:hover': { background: golden ? 'rgba(26, 26, 26, 0.45)' : dark ? 'rgba(212, 167, 71, 0.3)' : 'rgba(212, 167, 71, 0.15)' } }}>
        <ChevronDown size={20} strokeWidth={2.5} style={{ transform: direction === 'left' ? 'rotate(90deg)' : 'rotate(-90deg)', color: golden ? '#1A1A1A' : '#D4A747' }} />
      </Box>
    );

    // 2024 card with video + play button
    const VideoSpeakerSlide = ({ speaker, index }) => (
      <Box sx={{ minWidth: '100%', px: { xs: 2, md: 3 }, boxSizing: 'border-box', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ borderRadius: '10px', overflow: 'hidden', background: 'rgba(255,255,255,0.05)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', maxWidth: 440, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, height: { xs: 'auto', sm: 360 } }}>
            {/* Video side */}
            <Box sx={{ flex: { xs: '0 0 auto', sm: '0 0 45%' }, position: 'relative', background: '#111', height: { xs: 240, sm: '100%' } }}>
              <Box
                component="video"
                ref={(el) => (videoRefs.current[index] = el)}
                src={speaker.video}
                controls
                preload="metadata"
                onPause={handleVideoPause}
                onEnded={handleVideoPause}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: playingVideo === index ? 1 : 0.7 }}
              />
              {playingVideo !== index && (
                <Box
                  onClick={(e) => { e.stopPropagation(); handleVideoPlay(index); }}
                  sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(26,26,26,0.3), rgba(43,43,43,0.5))', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2 }}
                >
                  <Box sx={{ width: 50, height: 50, borderRadius: '50%', background: '#D4A747', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(212,167,71,0.4)' }}>
                    <Play size={20} color="white" fill="white" />
                  </Box>
                </Box>
              )}
            </Box>
            {/* Content side */}
            <Box sx={{ flex: 1, p: { xs: 2.5, sm: 3 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', background: '#1A1A1A', border: '3px solid #D4A747', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, boxShadow: '0 4px 12px rgba(212, 167, 71, 0.25)' }}>
                <img src={speaker.logo} alt="" style={{ width: '30px', height: '30px', objectFit: 'contain' }} />
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.15rem' }, color: '#F4E4BC', mb: 0.5, lineHeight: 1.2 }}>
                {speaker.name}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.82rem' }, color: 'rgba(212, 167, 71, 0.8)', fontWeight: 600 }}>
                {speaker.title}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );

    // 2025 card with image
    const ImageSpeakerSlide = ({ speaker }) => (
      <Box sx={{ minWidth: '100%', px: { xs: 2, md: 3 }, boxSizing: 'border-box', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ borderRadius: '10px', overflow: 'hidden', background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 16px rgba(0,0,0,0.3)', maxWidth: 440, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, height: { xs: 'auto', sm: 360 } }}>
            {/* Image side */}
            <Box sx={{ flex: { xs: '0 0 auto', sm: '0 0 45%' }, background: '#111', height: { xs: 240, sm: '100%' } }}>
              <img src={speaker.image} alt={speaker.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </Box>
            {/* Content side */}
            <Box sx={{ flex: 1, p: { xs: 2.5, sm: 3 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', background: '#1A1A1A', border: '3px solid #D4A747', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, boxShadow: '0 4px 12px rgba(212, 167, 71, 0.25)' }}>
                <img src={speaker.logo} alt="" style={{ width: '30px', height: '30px', objectFit: 'contain' }} />
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.15rem' }, color: '#F4E4BC', mb: 0.5, lineHeight: 1.2 }}>
                {speaker.name}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.82rem' }, color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 }}>
                {speaker.title}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );

    return (
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        {/* Section heading */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 1.5, fontSize: '0.875rem' }}>
            {t('sponsors.overline')}
          </Typography>
          <Typography variant="h2" sx={{ background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 900, mb: 2 }}>
            {t('sponsors.sectionTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'typography.main', maxWidth: 700, mx: 'auto' }}>
            {t('sponsors.subtitle')}
          </Typography>
        </Box>

        {/* Split view */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, position: 'relative', minHeight: { md: 520 } }}>

          {/* Left - 2024 (Dark) */}
          <Box sx={{ flex: '1 1 52%', maxWidth: { md: '52%' }, background: 'linear-gradient(135deg, #1A1A1A 0%, #0D0D0D 100%)', py: { xs: 5, md: 7 }, position: 'relative', clipPath: { xs: 'none', md: 'polygon(0 0, 100% 0, 95% 100%, 0 100%)' }, zIndex: 1 }}>
            {/* Year label */}
            <Typography sx={{ textAlign: 'center', fontSize: '0.65rem', fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase', color: 'rgba(212, 167, 71, 0.7)', mb: 3 }}>
              {t('sponsors.label2024')}
            </Typography>
            {/* Carousel */}
            <Box sx={{ overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', transform: `translateX(-${slide2024 * 100}%)`, transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                {speakers2024.map((speaker, i) => (
                  <VideoSpeakerSlide key={i} speaker={speaker} index={i} />
                ))}
              </Box>
            </Box>
            {/* Nav */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 3 }}>
              <ArrowBtn onClick={prev2024} direction="left" dark={true} />
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                {speakers2024.map((_, i) => (
                  <Box key={i} onClick={() => setSlide2024(i)} sx={{ width: slide2024 === i ? 20 : 8, height: 8, borderRadius: '4px', background: slide2024 === i ? '#D4A747' : 'rgba(255,255,255,0.15)', cursor: 'pointer', transition: 'all 0.3s ease' }} />
                ))}
              </Box>
              <ArrowBtn onClick={next2024} direction="right" dark={true} />
            </Box>
          </Box>

          {/* Right - 2025 (Golden) */}
          <Box sx={{ flex: '1 1 52%', maxWidth: { md: '52%' }, background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)', py: { xs: 5, md: 7 }, position: 'relative', clipPath: { xs: 'none', md: 'polygon(3% 0, 100% 0, 100% 100%, 0 100%)' }, ml: { md: '-4%' } }}>
            {/* Year label */}
            <Typography sx={{ textAlign: 'center', fontSize: '0.65rem', fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.85)', mb: 3 }}>
              {t('sponsors.label2025')}
            </Typography>
            {/* Carousel */}
            <Box sx={{ overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', transform: `translateX(-${slide2025 * 100}%)`, transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                {speakers2025.map((speaker, i) => (
                  <ImageSpeakerSlide key={i} speaker={speaker} />
                ))}
              </Box>
            </Box>
            {/* Nav */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 3 }}>
              <ArrowBtn onClick={prev2025} direction="left" golden={true} />
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                {speakers2025.map((_, i) => (
                  <Box key={i} onClick={() => setSlide2025(i)} sx={{ width: slide2025 === i ? 20 : 8, height: 8, borderRadius: '4px', background: slide2025 === i ? '#1A1A1A' : 'rgba(255,255,255,0.4)', cursor: 'pointer', transition: 'all 0.3s ease' }} />
                ))}
              </Box>
              <ArrowBtn onClick={next2025} direction="right" golden={true} />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // About Section
  const AboutSection = () => {
    const [showSecondImage, setShowSecondImage] = useState(false);

    useEffect(() => {
      const interval = setInterval(() => {
        setShowSecondImage((prev) => !prev);
      }, 5000);
      return () => clearInterval(interval);
    }, []);

    // Icon mapping
    const iconMap = {
      Trophy,
      Lightbulb,
      Users
    };

    const keyPoints = [
      { title: t('about.keyPoints.recognition.title'), description: t('about.keyPoints.recognition.description'), icon: iconMap['Trophy'] },
      { title: t('about.keyPoints.innovation.title'), description: t('about.keyPoints.innovation.description'), icon: iconMap['Lightbulb'] },
      { title: t('about.keyPoints.community.title'), description: t('about.keyPoints.community.description'), icon: iconMap['Users'] }
    ];

    return (
      <Box sx={{ pb: { xs: 6, md: 8 }, backgroundColor: '#FFFFFF' }}>
        {/* Black section with year label + images */}
        <Box sx={{ backgroundColor: '#000', pb: 0 }}>
          {/* Year label */}
          <Box sx={{ textAlign: 'center', py: { xs: 3, md: 4 } }}>
            {/* Static heading */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 1.5 }}>
              <Box sx={{ width: { xs: 40, md: 60 }, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(212, 167, 71, 0.6))' }} />
              <Star size={14} color="#D4A747" />
              <Box sx={{ width: { xs: 40, md: 60 }, height: '1px', background: 'linear-gradient(90deg, rgba(212, 167, 71, 0.6), transparent)' }} />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: '0.65rem', md: '0.75rem' },
                fontWeight: 600,
                color: 'rgba(212, 167, 71, 0.7)',
                letterSpacing: 6,
                textTransform: 'uppercase',
                mb: 1,
              }}
            >
              {t('about.glimpseOverline')}
            </Typography>
            <Typography
              key={showSecondImage ? '2025' : '2024'}
              component={motion.p}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              sx={{
                fontSize: { xs: '2rem', md: '2.8rem' },
                fontWeight: 800,
                background: 'linear-gradient(180deg, #F4E4BC 0%, #D4A747 50%, #B8901C 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: 3,
              }}
            >
              {showSecondImage ? t('about.awards2025') : t('about.awards2024')}
            </Typography>
            {/* Decorative line below */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 1.5 }}>
              <Box sx={{ width: { xs: 30, md: 50 }, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(212, 167, 71, 0.4))' }} />
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(212, 167, 71, 0.5)' }} />
              <Box sx={{ width: { xs: 30, md: 50 }, height: '1px', background: 'linear-gradient(90deg, rgba(212, 167, 71, 0.4), transparent)' }} />
            </Box>
          </Box>
          {/* Image container */}
          <Box sx={{ position: 'relative', overflow: 'hidden', maxHeight: { xs: 320, md: 460 } }}>
            {/* Image 1: image3-bg.png (2024 Awards) - shown first */}
            <img
              src={image3bg}
              alt="Bhoomee Awards 2024"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center 30%',
                display: 'block',
                opacity: showSecondImage ? 0 : 1,
                filter: showSecondImage ? 'blur(10px)' : 'blur(0px)',
                transition: 'opacity 1.2s ease-in-out, filter 1.2s ease-in-out',
              }}
            />
            {/* Image 2: lamp-lighting.png (2025 Awards) */}
            <img
              src="/images/lamp-lighting.png"
              alt="Bhoomee Awards 2025 Inauguration"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center top',
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                opacity: showSecondImage ? 1 : 0,
                filter: showSecondImage ? 'blur(0px)' : 'blur(10px)',
                transition: 'opacity 1.2s ease-in-out, filter 1.2s ease-in-out',
              }}
            />
            {/* Dark overlay - dark at top, light at bottom */}
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1, background: 'linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.85) 20%, rgba(0, 0, 0, 0.4) 45%, rgba(0, 0, 0, 0.15) 70%, rgba(0, 0, 0, 0.05) 100%)' }} />
          </Box>
        </Box>

        {/* Floating content card overlapping the banner */}
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Box
              sx={{
                mt: { xs: -4, md: -6 },
                mx: { xs: 2, md: 0 },
                position: 'relative',
                zIndex: 2,
                background: '#FFFFFF',
                borderRadius: '16px',
                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.1)',
                p: { xs: 3, md: 5 },
              }}
            >
              <Grid container spacing={4} alignItems="center">
                {/* Left: Content */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Box sx={{ width: 24, height: '2px', background: 'linear-gradient(90deg, #D4A747, #B8901C)' }} />
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#D4A747', letterSpacing: 3, textTransform: 'uppercase' }}>
                      {t('about.overline')}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: { xs: '1.4rem', md: '1.7rem' }, fontWeight: 800, lineHeight: 1.2, mb: 1, background: 'linear-gradient(135deg, #2B2B2B 0%, #1A1A1A 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {t('about.title')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'typography.main', mb: 2, lineHeight: 1.7, fontSize: { xs: '0.88rem', md: '0.95rem' } }}>
                    {t('about.description')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {keyPoints.map((point, index) => {
                      const Icon = point.icon;
                      return (
                        <Box key={index} sx={{ flex: '1 1 0', minWidth: 0, textAlign: 'center', p: 1.5, borderRadius: '10px', border: '1px solid rgba(212, 167, 71, 0.15)', transition: 'all 0.3s ease', '&:hover': { borderColor: 'rgba(212, 167, 71, 0.3)', background: 'rgba(212, 167, 71, 0.04)' } }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: 'linear-gradient(135deg, rgba(212, 167, 71, 0.12) 0%, rgba(184, 144, 28, 0.08) 100%)', border: '1px solid rgba(212, 167, 71, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#D4A747', mx: 'auto', mb: 0.75 }}>
                            <Icon size={16} />
                          </Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', lineHeight: 1.2, color: '#1A1A1A', mb: 0.25 }}>
                            {point.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'typography.main', lineHeight: 1.4, fontSize: '0.72rem' }}>
                            {point.description}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>

                {/* Right: Bento grid of 6 images */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gridTemplateRows: 'repeat(3, 1fr)',
                    gap: '6px',
                    height: { xs: 300, md: 400 },
                  }}>
                    {/* 2025 - large image spanning 2 cols, 2 rows */}
                    <Box sx={{ gridColumn: '1 / 3', gridRow: '1 / 3', position: 'relative', overflow: 'hidden', borderRadius: '10px', '&:hover img': { transform: 'scale(1.04)' } }}>
                      <img src="/images/dignitaries-on-stage.png" alt="2025 Awards" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease' }} />
                      <Box sx={{ position: 'absolute', bottom: 6, left: 6, backgroundColor: 'rgba(212, 167, 71, 0.85)', px: 1, py: 0.3, borderRadius: '4px' }}>
                        <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#1A1A1A', letterSpacing: 1 }}>2025</Typography>
                      </Box>
                    </Box>
                    {/* 2025 - top right */}
                    <Box sx={{ gridColumn: '3', gridRow: '1', overflow: 'hidden', borderRadius: '10px', '&:hover img': { transform: 'scale(1.04)' } }}>
                      <img src={image3} alt="2025 Awards" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease' }} />
                    </Box>
                    {/* 2025 - mid right */}
                    <Box sx={{ gridColumn: '3', gridRow: '2', overflow: 'hidden', borderRadius: '10px', '&:hover img': { transform: 'scale(1.04)' } }}>
                      <img src="/images/event-winners-14.png" alt="2025 Awards" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease' }} />
                    </Box>
                    {/* 2024 - bottom row, 3 images */}
                    {[image1, image2, "/images/dignitaries-group.JPG"].map((src, i) => (
                      <Box key={i} sx={{ gridColumn: `${i + 1}`, gridRow: '3', position: 'relative', overflow: 'hidden', borderRadius: '10px', '&:hover img': { transform: 'scale(1.04)' } }}>
                        <img src={src} alt="2024 Awards" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease' }} />
                        {i === 0 && (
                          <Box sx={{ position: 'absolute', bottom: 6, left: 6, backgroundColor: 'rgba(212, 167, 71, 0.85)', px: 1, py: 0.3, borderRadius: '4px' }}>
                            <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#1A1A1A', letterSpacing: 1 }}>2024</Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </motion.div>
        </Container>
      </Box>
    );
  };

  // Award Categories Section
  const AwardCategoriesSection = () => {
    // Icon mapping
    const iconMap = {
      Users,
      TrendingUp,
      Lightbulb
    };

    const categoryGroups = [
      {
        title: t('awardCategories.categoryGroups.farmer.title'),
        description: t('awardCategories.categoryGroups.farmer.description'),
        icon: iconMap['Users'],
        gradient: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
        awardsLength: 10,
        role: 'farmer'
      },
      {
        title: t('awardCategories.categoryGroups.fpo.title'),
        description: t('awardCategories.categoryGroups.fpo.description'),
        icon: iconMap['TrendingUp'],
        gradient: 'linear-gradient(135deg, #2B2B2B 0%, #1A1A1A 100%)',
        awardsLength: 5,
        role: 'fpo'
      },
      {
        title: t('awardCategories.categoryGroups.innovators.title'),
        description: t('awardCategories.categoryGroups.innovators.description'),
        icon: iconMap['Lightbulb'],
        gradient: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
        awardsLength: 5,
        role: 'innovation'
      }
    ];

    return (
      <Box sx={{ py: { xs: 8, md: 8 }, backgroundColor: 'white' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: '0.875rem',
              }}
            >
              {t('awardCategories.overline')}
            </Typography>
            <Typography variant="h2" sx={{
              background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 900,
              mb: 2
            }}>
              {t('awardCategories.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'typography.main', maxWidth: 700, mx: 'auto' }}>
              {t('awardCategories.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {categoryGroups.map((group, groupIndex) => {
              const Icon = group.icon;
              const isGold = groupIndex === 0 || groupIndex === 2;

              return (
                <Grid size={{ xs: 12, lg: 4 }} key={groupIndex}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: groupIndex * 0.2 }}
                    viewport={{ once: true }}
                    style={{ height: '100%', display: 'flex' }}
                  >
                    <Card
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: '10px',
                        background: '#FFFFFF',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                          '& .header-gradient': {
                            transform: 'scale(1.02)',
                          },
                          '& .floating-icon': {
                            transform: 'rotate(10deg) scale(1.1)',
                          }
                        },
                      }}
                    >
                      {/* Header Section with Gradient */}
                      <Box
                        className="header-gradient"
                        sx={{
                          background: group.gradient,
                          p: { xs: 3, lg: 4 },
                          pb: { xs: 3, lg: 5 },
                          minHeight: { xs: 'auto', lg: 380 },
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                          transition: 'transform 0.4s ease',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '30px',
                            background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.3))',
                          }
                        }}
                      >
                        {/* Decorative Pattern */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: { xs: '100px', lg: '200px' },
                            height: { xs: '100px', lg: '200px' },
                            opacity: 0.1,
                            background: `radial-gradient(circle, ${isGold ? '#FFF' : '#D4A747'} 1px, transparent 1px)`,
                            backgroundSize: '20px 20px',
                          }}
                        />

                        {/* Icon Container */}
                        <Box
                          className="floating-icon"
                          sx={{
                            width: { xs: 56, lg: 72 },
                            height: { xs: 56, lg: 72 },
                            borderRadius: '20px',
                            background: isGold ? 'rgba(255,255,255,0.2)' : 'rgba(212,167,71,0.2)',
                            backdropFilter: 'blur(10px)',
                            border: `2px solid ${isGold ? 'rgba(255,255,255,0.3)' : 'rgba(212,167,71,0.3)'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: { xs: 2, lg: 3 },
                            transition: 'transform 0.4s ease',
                          }}
                        >
                          <Icon size={36} color={isGold ? '#FFFFFF' : '#D4A747'} strokeWidth={2} />
                        </Box>

                        {/* Title & Description */}
                        <Typography
                          variant="h3"
                          sx={{
                            color: isGold ? '#FFFFFF' : '#D4A747',
                            fontWeight: 800,
                            mb: { xs: 1.5, lg: 2 },
                            fontSize: { xs: '1.5rem', lg: '2rem' },
                          }}
                        >
                          {group.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: isGold ? 'rgba(255,255,255,0.9)' : 'rgba(212,167,71,0.9)',
                            lineHeight: 1.6,
                            fontSize: { xs: '0.875rem', lg: '0.95rem' },
                          }}
                        >
                          {group.description}
                        </Typography>
                      </Box>

                      {/* Awards List */}
                      <CardContent sx={{ p: { xs: 3, lg: 4 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        {/* Footer with Learn More Button */}
                        <Box sx={{ mt: 'auto' }}>
                          <Button
                            component={Link}
                            to={`/${currentLang}/nominations?role=${group.role}#categories`}
                            endIcon={<ArrowRight size={18} />}
                            onClick={() => {
                              logCustomEvent('learn_more_clicked', {
                                category_group: group.title,
                                location: 'award_categories_section'
                              });
                            }}
                            sx={{
                              width: '100%',
                              py: 1.5,
                              borderRadius: '10px',
                              background: isGold
                                ? 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)'
                                : 'linear-gradient(135deg, #2B2B2B 0%, #1A1A1A 100%)',
                              color: '#FFFFFF',
                              fontWeight: 600,
                              fontSize: '0.9rem',
                              textTransform: 'none',
                              border: '1px solid',
                              borderColor: isGold ? 'rgba(212, 167, 71, 0.2)' : 'rgba(43, 43, 43, 0.15)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: 'transparent',
                                transform: 'translateY(-2px)',
                                boxShadow: isGold
                                  ? '0 8px 24px rgba(212, 167, 71, 0.3)'
                                  : '0 8px 24px rgba(0, 0, 0, 0.2)',
                                '& .MuiButton-endIcon': {
                                  transform: 'translateX(4px)',
                                },
                              },
                              '& .MuiButton-endIcon': {
                                transition: 'transform 0.3s ease',
                              },
                            }}
                          >
                            {t('common:common.learnMore')}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>
    );
  };

  // Why Choose Section
  const WhyChooseSection = () => {
    // Icon mapping
    const iconMap = {
      Trophy,
      Users,
      TrendingUp,
      Lightbulb,
      Award,
      Star
    };

    const reasons = [
      { title: t('whyChoose.reasons.credibility.title'), description: t('whyChoose.reasons.credibility.description'), icon: iconMap['Trophy'] },
      { title: t('whyChoose.reasons.expertJury.title'), description: t('whyChoose.reasons.expertJury.description'), icon: iconMap['Users'] },
      { title: t('whyChoose.reasons.growth.title'), description: t('whyChoose.reasons.growth.description'), icon: iconMap['TrendingUp'] },
      { title: t('whyChoose.reasons.innovation.title'), description: t('whyChoose.reasons.innovation.description'), icon: iconMap['Lightbulb'] },
      { title: t('whyChoose.reasons.mediaCoverage.title'), description: t('whyChoose.reasons.mediaCoverage.description'), icon: iconMap['Award'] },
      { title: t('whyChoose.reasons.networking.title'), description: t('whyChoose.reasons.networking.description'), icon: iconMap['Star'] }
    ];

    return (
      <Box sx={{ py: { xs: 8, md: 6}, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: '0.875rem',
              }}
            >
              {t('whyChoose.overline')}
            </Typography>
            <Typography variant="h2" sx={{ background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {t('whyChoose.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'typography.main', mt: 2, maxWidth: 600, mx: 'auto' }}>
              {t('whyChoose.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {reasons.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -10 }}
                  >
                    <Card
                      sx={{
                        p: 3,
                        height: 320,
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                        borderRadius: "10px",
                        backgroundColor: '#FFFFFF',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            mb: 2,
                          }}
                        >
                          <Icon size={28} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {reason.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'typography.main', flex: 1 }}>
                          {reason.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>
    );
  };

  // Jury Composition Section
  const JuryCompositionSection = () => {
    // Icon mapping
    const iconMap = {
      Award,
      Briefcase,
      Users,
      Heart,
      FileText,
      TrendingUp
    };

    const juryCategories = [
      { title: t('juryComposition.juryCategories.academics.title'), description: t('juryComposition.juryCategories.academics.description'), icon: iconMap['Award'] },
      { title: t('juryComposition.juryCategories.industry.title'), description: t('juryComposition.juryCategories.industry.description'), icon: iconMap['Briefcase'] },
      { title: t('juryComposition.juryCategories.social.title'), description: t('juryComposition.juryCategories.social.description'), icon: iconMap['Heart'] }
    ];

    return (
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('juryComposition.overline')}
              </Typography>
              <Typography variant="h2" sx={{
                background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 900,
                mb: 2
              }}>
                {t('juryComposition.title')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'typography.main', maxWidth: 700, mx: 'auto' }}>
                {t('juryComposition.subtitle')}
              </Typography>
            </Box>

            <Grid container spacing={4} sx={{ alignItems: 'stretch' }}>
              {juryCategories.map((category, index) => {
                const Icon = category.icon;
                const isGold = index % 2 === 0;

                return (
                  <Grid size={{ xs: 12, lg: 4 }} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      style={{ height: '100%' }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          minHeight: 280,
                          mb: 2,
                          position: 'relative',
                          overflow: 'visible',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {/* Main Card */}
                        <Box
                          sx={{
                            height: '100%',
                            background: '#FFFFFF',
                            borderRadius: '15px',
                            p: 4,
                            position: 'relative',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                            border: '1px solid',
                            borderColor: isGold ? 'rgba(212, 167, 71, 0.12)' : 'rgba(43, 43, 43, 0.08)',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {/* Floating Icon Badge */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -24,
                              right: 24,
                              width: 50,
                              height: 50,
                              borderRadius: '50%',
                              background: isGold
                                ? 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)'
                                : 'linear-gradient(135deg, #2B2B2B 0%, #1A1A1A 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                              zIndex: 2,
                            }}
                          >
                            <Icon size={20} color={isGold ? '#1A1A1A' : '#FFFFFF'} strokeWidth={2.5} />
                          </Box>

                          {/* Vertical Accent Line */}
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: '20%',
                              bottom: '20%',
                              width: '4px',
                              background: isGold
                                ? 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)'
                                : 'linear-gradient(135deg, #2B2B2B 0%, #1A1A1A 100%)',
                              borderRadius: '0 4px 4px 0',
                            }}
                          />

                          {/* Content Container */}
                          <Box sx={{ pl: 2, pt: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
                            {/* Title with Underline */}
                            <Box sx={{ mb: 2.5 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: 'typography.main',
                                  mb: 0.5,
                                  fontSize: '1.2rem',
                                  lineHeight: 1.3,
                                }}
                              >
                                {category.title}
                              </Typography>
                              <Box
                                sx={{
                                  width: '50px',
                                  height: '3px',
                                  background: isGold
                                    ? 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)'
                                    : 'linear-gradient(135deg, #2B2B2B 0%, #1A1A1A 100%)',
                                  borderRadius: '2px',
                                }}
                              />
                            </Box>

                            {/* Description */}
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'typography.secondary',
                                lineHeight: 1.8,
                                fontSize: '0.95rem',
                                flex: 1,
                              }}
                            >
                              {category.description}
                            </Typography>
                          </Box>

                          {/* Bottom Corner Dot Pattern */}
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 16,
                              right: 16,
                              display: 'flex',
                              gap: 0.5,
                            }}
                          >
                            {[1, 2, 3].map((dot) => (
                              <Box
                                key={dot}
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  background: isGold ? 'rgba(212, 167, 71, 0.3)' : 'rgba(43, 43, 43, 0.2)',
                                }}
                              />
                            ))}
                          </Box>

                          {/* Subtle Background Pattern */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              width: '150px',
                              height: '150px',
                              opacity: 0.03,
                              background: `radial-gradient(circle, ${isGold ? '#D4A747' : '#2B2B2B'} 1px, transparent 1px)`,
                              backgroundSize: '12px 12px',
                              pointerEvents: 'none',
                            }}
                          />
                        </Box>
                      </Box>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>
        </Container>
      </Box>
    );
  };

  // Winners Gallery Section
  const WinnersGallery = () => {
    const [activeGalleryYear, setActiveGalleryYear] = useState('2025');

    const winners2025 = [
      { src: '/images/event-winners/winner1.png', name: 'Award Recipient', rotate: -3, big: true },
      { src: '/images/event-winners/winner5.png', name: 'Award Recipient', rotate: 2, big: true },
      { src: '/images/event-winners/winner3.png', name: 'Award Recipient', rotate: -1.5, big: false },
      { src: '/images/event-winners/winner4.png', name: 'Award Recipient', rotate: 3, big: false },
      { src: '/images/event-winners/winner2.png', name: 'Award Recipient', rotate: -2, big: true },
    ];

    const winners2024 = [
      { src: 'https://bhoomee-awards-submissions.s3.ap-south-1.amazonaws.com/gallery/2024/Gopal+Patil%2C+Bidar%2C+Zonal+Award.JPG', name: 'Award Recipient', rotate: -3, big: true },
      { src: 'https://bhoomee-awards-submissions.s3.ap-south-1.amazonaws.com/gallery/2024/Ramesh+kumar+S%2C+Ramanagar+Zonal+Award.JPG', name: 'Award Recipient', rotate: 2, big: true },
      { src: 'https://bhoomee-awards-submissions.s3.ap-south-1.amazonaws.com/gallery/2024/Prashanth+Chennappa+Negur%2C+Belagavi.JPG', name: 'Award Recipient', rotate: -1.5, big: false },
      { src: 'https://bhoomee-awards-submissions.s3.ap-south-1.amazonaws.com/gallery/2024/Manjula+H+R%2C+B%27luru+Rural+District.JPG', name: 'Award Recipient', rotate: 3, big: false },
      { src: 'https://bhoomee-awards-submissions.s3.ap-south-1.amazonaws.com/gallery/2024/Mangala+K+Nilagunda%2C+Gadag.JPG', name: 'Award Recipient', rotate: -2, big: true },
    ];

    // Auto-slide between years every 6 seconds
    useEffect(() => {
      const interval = setInterval(() => {
        setActiveGalleryYear((prev) => prev === '2025' ? '2024' : '2025');
      }, 6000);
      return () => clearInterval(interval);
    }, []);

    const winners = activeGalleryYear === '2025' ? winners2025 : winners2024;

    return (
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: 'linear-gradient(160deg, #1A1A1A 0%, #0D0D0D 60%, #1A1A1A 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background decorative elements */}
        <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle at 25% 25%, #D4A747 1px, transparent 1px), radial-gradient(circle at 75% 75%, #D4A747 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <Box sx={{ position: 'absolute', top: '10%', right: '5%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212, 167, 71, 0.06) 0%, transparent 70%)' }} />
        <Box sx={{ position: 'absolute', bottom: '10%', left: '5%', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212, 167, 71, 0.04) 0%, transparent 70%)' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
            <Typography variant="overline" sx={{ color: '#D4A747', fontWeight: 600, letterSpacing: 3, fontSize: '0.85rem' }}>
              {t('winnersGallery.overline')}
            </Typography>
            <Typography variant="h2" sx={{ background: 'linear-gradient(180deg, #F4E4BC 0%, #D4A747 50%, #B8901C 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 900, mb: 1.5 }}>
              {t('winnersGallery.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(244, 228, 188, 0.6)', maxWidth: 550, mx: 'auto' }}>
              {t('winnersGallery.subtitle')}
            </Typography>
          </Box>

          {/* Year indicator + cards */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeGalleryYear}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 2, md: 3 } }}>
                <Typography sx={{
                  fontSize: { xs: '0.7rem', md: '0.75rem' },
                  fontWeight: 700,
                  letterSpacing: 4,
                  textTransform: 'uppercase',
                  color: 'rgba(212, 167, 71, 0.7)',
                }}>
                  — {activeGalleryYear} Recipients —
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1.5, md: 3 }, perspective: '1200px', overflowX: { xs: 'auto', md: 'visible' }, pb: 2 }}>
              {winners.map((winner, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30, scale: 0.92, rotateZ: 0 }}
                  animate={{ opacity: 1, y: 0, scale: 1, rotateZ: winner.rotate }}
                  transition={{ duration: 0.8, delay: 0.15 + i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
                  whileHover={{ scale: 1.06, rotateZ: 0, y: -12, zIndex: 10 }}
                  style={{ zIndex: i, flexShrink: 0 }}
                >
                  <Box
                    sx={{
                      width: { xs: 150, sm: 180, md: 210 },
                      background: '#FFFFFF',
                      borderRadius: '6px',
                      p: '10px 10px 32px 10px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0,0,0,0.2)',
                      transition: 'box-shadow 0.4s ease',
                      position: 'relative',
                      '&:hover': { boxShadow: '0 24px 64px rgba(212, 167, 71, 0.35), 0 4px 16px rgba(0,0,0,0.3)' },
                    }}
                  >
                    <Box sx={{ overflow: 'hidden', borderRadius: '3px', height: { xs: 180, sm: 220, md: 260 } }}>
                      <img src={winner.src} alt={`Winner ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '6px', px: '2px' }}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#999', letterSpacing: 1 }}>
                        {activeGalleryYear}
                      </Typography>
                      <Box sx={{ width: 22, height: 22, borderRadius: '50%', background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(212, 167, 71, 0.4)' }}>
                        <Trophy size={12} color="#FFF" />
                      </Box>
                    </Box>
                  </Box>
                </motion.div>
              ))}
              </Box>
            </motion.div>
          </AnimatePresence>

          {/* View All Winners Button */}
          <Box sx={{ textAlign: 'center', mt: { xs: 6, md: 8 } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                component={Link}
                to={`/${currentLang}/winners`}
                variant="outlined"
                size="large"
                endIcon={<ArrowRight size={20} />}
                onClick={() => {
                  logCustomEvent('view_all_winners_clicked', { location: 'winners_gallery_section' });
                }}
                sx={{
                  color: '#D4A747',
                  borderColor: 'rgba(212, 167, 71, 0.4)',
                  border: '1.5px solid',
                  px: 5,
                  py: 1.5,
                  fontSize: '1.05rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '12px',
                  letterSpacing: 0.5,
                  transition: 'all 0.4s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                    color: '#1A1A1A',
                    borderColor: '#D4A747',
                    boxShadow: '0 12px 40px rgba(212, 167, 71, 0.3)',
                  },
                }}
              >
                {t('winnersGallery.viewAllWinners')}
              </Button>
            </motion.div>
          </Box>
        </Container>
      </Box>
    );
  };

  // Path to Awards Section
  const PathToAwards = () => {
    const { ref } = useInView({ threshold: 0.3 });

    // Icon mapping
    const iconMap = {
      UserPlus,
      FileText,
      Search,
      Gavel,
      Microscope,
      PartyPopper
    };

    const steps = [
      {
        title: t('pathToAwards.steps.nominations.title'),
        description: t('pathToAwards.steps.nominations.description'),
        detail: t('pathToAwards.steps.nominations.detail'),
        estimatedTime: t('pathToAwards.steps.nominations.date'),
        icon: iconMap['UserPlus']
      },
      {
        title: t('pathToAwards.steps.deadline.title'),
        description: t('pathToAwards.steps.deadline.description'),
        detail: t('pathToAwards.steps.deadline.detail'),
        estimatedTime: t('pathToAwards.steps.deadline.date'),
        icon: iconMap['FileText']
      },
      {
        title: t('pathToAwards.steps.screening.title'),
        description: t('pathToAwards.steps.screening.description'),
        detail: t('pathToAwards.steps.screening.detail'),
        estimatedTime: t('pathToAwards.steps.screening.date'),
        icon: iconMap['Search']
      },
      {
        title: t('pathToAwards.steps.internal.title'),
        description: t('pathToAwards.steps.internal.description'),
        detail: t('pathToAwards.steps.internal.detail'),
        estimatedTime: t('pathToAwards.steps.internal.date'),
        icon: iconMap['Gavel']
      },
      {
        title: t('pathToAwards.steps.preparation.title'),
        description: t('pathToAwards.steps.preparation.description'),
        detail: t('pathToAwards.steps.preparation.detail'),
        estimatedTime: t('pathToAwards.steps.preparation.date'),
        icon: iconMap['Microscope']
      },
      {
        title: t('pathToAwards.steps.ceremony.title'),
        description: t('pathToAwards.steps.ceremony.description'),
        detail: t('pathToAwards.steps.ceremony.detail'),
        estimatedTime: t('pathToAwards.steps.ceremony.date'),
        icon: iconMap['PartyPopper']
      }
    ];

    return (
      <Box
        ref={ref}
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
            `,
          }}
        />
        
        {/* Floating Elements */}
        <Box
          sx={{
            position: 'absolute',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)',
            top: '10%',
            right: '10%',
            animation: 'float 6s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-20px)' },
            },
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'secondary.light',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('pathToAwards.overline')}
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F4E4BC 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 900,
                  mb: 2
                }}
              >
                {t('pathToAwards.title')}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  mt: 2,
                  maxWidth: 600,
                  mx: 'auto',
                  opacity: 0.9,
                  lineHeight: 1.6
                }}
              >
                {t('pathToAwards.subtitle')}
              </Typography>
            </Box>
          </motion.div>

          {/* Timeline Container */}
          <Box sx={{ position: 'relative', maxWidth: 1200, mx: 'auto' }}>
            {/* Progress Line */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: 4,
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 2,
                transform: 'translateY(-50%)',
                zIndex: 1,
                display: { xs: 'none', md: 'block' }
              }}
            />

            <Grid container spacing={{ xs: 3, md: 2 }} sx={{ position: 'relative', zIndex: 3, alignItems: 'stretch' }}>
              {steps.map((step, index) => {
                const Icon = step.icon;

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 50 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      style={{ height: '100%' }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          textAlign: 'center',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {/* Step Circle */}
                        <Box
                          sx={{
                            position: 'relative',
                            width: 70,
                            height: 80,
                            mx: 'auto',
                            mb: 3,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #FFFFFF 0%, #F4E4BC 100%)',
                            border: '3px solid #FFFFFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 20px 40px rgba(255, 255, 255, 0.3)',
                          }}
                        >
                          <Icon 
                            size={28} 
                            color="#D4A747"
                          />
                        </Box>

                        {/* Content Card */}
                        <Box
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            backdropFilter: 'blur(12px)',
                            borderRadius: 3,
                            p: 3,
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            minHeight: 360,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          {/* Title */}
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              mb: 1.5,
                              fontSize: '1rem',
                              lineHeight: 1.4,
                              textAlign: 'center',
                            }}
                          >
                            {step.title}
                          </Typography>

                          {/* Description */}
                          <Typography
                            variant="body2"
                            sx={{
                              opacity: 0.9,
                              mb: 2,
                              lineHeight: 1.6,
                              fontSize: '0.875rem',
                              textAlign: 'center',
                            }}
                          >
                            {step.description}
                          </Typography>

                          {/* Detail Content */}
                          <Typography
                            variant="caption"
                            sx={{
                              opacity: 0.8,
                              display: 'block',
                              lineHeight: 1.7,
                              fontSize: '0.8rem',
                              textAlign: 'center',
                              flex: 1,
                            }}
                          >
                            {step.detail}
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Container>
      </Box>
    );
  };

  return (
    <Box sx={{ overflowX: 'hidden', width: '100%' }}>
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
      </Helmet>
      <HeroSection />
      <AboutSection />
      <SponsorsSection />
      <AwardCategoriesSection />
      <WhyChooseSection />
      <WinnersGallery />
      <JuryCompositionSection />
      <PathToAwards />
    </Box>
  );
};

export default Home;